import { inject, Injectable } from '@angular/core';
import { createEffect } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import {
  EffectsBase,
  fromSocketEvent,
  SocketActions,
} from '@ps/core-data-access';
import {
  BuyerProgressChannelV3,
  SurveyCompletesChange,
  SurveyScoreChange,
  SurveyStatusChange,
} from '@ps/survey-utils';
import {
  concatMap,
  distinctUntilChanged,
  filter,
  from,
  map,
  mergeMap,
  of,
  pairwise,
  startWith,
} from 'rxjs';

import {
  selectActiveTabId,
  selectSurveyIds,
} from './survey-list/survey-list.selectors';
import { SURVEY_SOCKET_EVENTS } from '../utils/constants/survey-socket.constants';
import {
  SurveyListActions,
  SurveyListSocketUpdateActions,
} from './survey-list';
import { SurveyDashboardSocketDto } from '../api/dtos/survey-socket.dto';
import { SurveyDashboardSocketMapper } from '../api/mapper/survey-socket.mapper';
import { concatLatestFrom } from '@ngrx/operators';

/**
 * Membership matches by the survey-id set, not array order. The list reorders
 * on every sort/pagination change while subscribing to the same surveys, so an
 * order-sensitive compare would churn unsubscribe→subscribe needlessly.
 */
function sameSurveyIdSet(left: number[], right: number[]): boolean {
  if (left.length !== right.length) {
    return false;
  }
  const known = new Set(left);
  return right.every((id) => known.has(id));
}

@Injectable()
export class SurveySocketEffects extends EffectsBase {
  private readonly _store = inject(Store);
  private readonly _dashboardMapper = new SurveyDashboardSocketMapper();

  public readonly syncSurveyRooms$ = createEffect(() =>
    this._store.select(selectSurveyIds).pipe(
      distinctUntilChanged(sameSurveyIdSet),
      startWith<number[]>([]),
      pairwise(),
      filter(([previous, current]) => !sameSurveyIdSet(previous, current)),
      concatMap(([previous, current]) =>
        from(this._roomCommands(previous, current)),
      ),
    ),
  );

  public readonly surveyInProgressChange$ = createEffect(() =>
    fromSocketEvent<BuyerProgressChannelV3>(
      SURVEY_SOCKET_EVENTS.BUYER_IN_PROGRESS_CHANNEL,
    ).pipe(
      map(({ surveyId, actualBuyerInProgress }) =>
        SurveyListActions.updateSurvey({
          surveyId,
          survey: { inProgress: actualBuyerInProgress },
        }),
      ),
    ),
  );

  public readonly surveyCompleteChange$ = createEffect(() =>
    fromSocketEvent<SurveyCompletesChange>(
      SURVEY_SOCKET_EVENTS.SURVEY_COMPLETES,
    ).pipe(
      map(({ surveyId, completes }) =>
        SurveyListActions.updateSurvey({
          surveyId,
          survey: { completesRequired: completes },
        }),
      ),
    ),
  );

  public readonly surveyScoreChange$ = createEffect(() =>
    fromSocketEvent<SurveyScoreChange>(
      SURVEY_SOCKET_EVENTS.SURVEY_SCORE_CHANGE,
    ).pipe(
      map(({ surveyId, score, scorePercentage }) =>
        SurveyListActions.updateSurvey({
          surveyId,
          survey: { surveyScore: { score, scorePercentage } },
        }),
      ),
    ),
  );

  public readonly surveyStatusChange$ = createEffect(() =>
    fromSocketEvent<SurveyStatusChange>(
      SURVEY_SOCKET_EVENTS.SURVEY_STATUS_CHANGE,
    ).pipe(
      concatLatestFrom(() => this._store.select(selectActiveTabId)),
      mergeMap(([data, activeTabId]) => {
        const payload = {
          ...data,
          isAllTab: activeTabId === 'all',
        };
        return of(
          SurveyListSocketUpdateActions.filterSurveyByStatus(payload),
          SurveyListActions.updateSurvey({
            surveyId: data.surveyId,
            survey: { status: data.newStatus },
          }),
        );
      }),
    ),
  );

  public readonly dashboardDetailsChange$ = createEffect(() =>
    fromSocketEvent<SurveyDashboardSocketDto>(
      SURVEY_SOCKET_EVENTS.DASHBOARD_UPDATE,
    ).pipe(
      map((dto) =>
        SurveyListActions.updateSurvey(this._dashboardMapper.fromDto(dto)),
      ),
    ),
  );

  private _roomCommands(previous: number[], current: number[]): Action[] {
    const commands: Action[] = [];
    if (previous.length) {
      commands.push(
        SocketActions.leaveRoom({
          event: SURVEY_SOCKET_EVENTS.UNSUBSCRIBE_FROM_SURVEYS,
          payload: { surveyIds: previous },
        }),
      );
    }
    if (current.length) {
      commands.push(
        SocketActions.joinRoom({
          event: SURVEY_SOCKET_EVENTS.SUBSCRIBE_TO_SURVEYS,
          payload: { surveyIds: current },
        }),
      );
    }
    return commands;
  }
}
